/**
 * Marketplace (Salunas) API contracts — the ONE source of truth for request /
 * response shapes on `/api/v1/marketplace/**`.
 *
 * Mirrors `docs/api-contracts.md` (§1–§10). Rules:
 * - Fields that existed before the redesign keep their original optionality.
 * - Every field ADDED by the redesign is optional (`?`) and, where the doc says
 *   it can be empty, also `| null`, so the UI keeps working against a backend
 *   that hasn't shipped it yet. Never assume a new field is present.
 * - Times are salon wall-clock strings + an IANA `timezone` (no "Z"); money is
 *   a number + ISO-4217 currency, formatted client-side.
 *
 * `use-marketplace.ts` re-exports everything here, so existing
 * `import { … } from '../api/use-marketplace'` keeps working.
 */

// ─── Shared primitives ─────────────────────────────────────────────────

/** "YYYY-MM-DD", salon-local. */
export type IsoDate = string;
/** "HH:mm", salon-local. */
export type WallClockTime = string;
/** "YYYY-MM-DDTHH:mm[:ss]", salon-local wall clock, NO "Z" (see parseServerDate). */
export type WallClockDateTime = string;

/**
 * AT_LOCATION = in person at a fixed location.
 * MOBILE      = staff travels to the customer (#246/#247).
 * ONLINE      = video call via Google Meet (#249 A3+).
 */
export type BookingMode = 'AT_LOCATION' | 'MOBILE' | 'ONLINE';

/** Backend error body (BookingExceptionMapper shape). See `errors.ts`. */
export interface ApiErrorBody {
  code?: string;
  message?: string;
  /** Legacy shape used by the auth endpoints, e.g. `{"error":"Invalid or expired link"}`. */
  error?: string;
  details?: Record<string, unknown> | null;
  retryable?: boolean;
}

// ─── Catalogue (categories, cities, countries) ─────────────────────────

export interface MarketplaceSubcategory {
  key: string;
  displayName: string;
}

export interface MarketplaceCategory {
  key: string;
  displayName: string;
  icon: string | null;
  subcategories: MarketplaceSubcategory[];
}

export interface MarketplaceCity {
  name: string;
  salonCount: number;
  /** ISO 3166-1 alpha-2 (coming with backend 48fc1eb+); absent on older backends. */
  country?: string | null;
}

export interface MarketplaceCountry {
  code: string;
  salonCount: number;
}

// ─── §1 Search ─────────────────────────────────────────────────────────

/** Next free slots for a row / profile (§1). */
export interface NextAvailable {
  /** Salon-local date, e.g. "2026-10-02". */
  date: IsoDate;
  /** Same-day times, ≤ `nextSlots`, e.g. ["16:00","17:30"]. */
  slots: WallClockTime[];
  /** The service used to compute the slots (the featured matching service). */
  serviceId: string;
  /** IANA timezone, e.g. "Europe/Brussels". */
  timezone: string;
}

/** A service that matched the search (≤ 2 per row). */
export interface MatchingService {
  id: string;
  name: string;
  durationMinutes: number;
  price: number;
  /** Render as "from €95". */
  priceFrom: boolean;
}

/** One search row. Pre-redesign fields keep their shape; §1 fields are optional. */
export interface SalonSearchResult {
  tenantId: string;
  /** Stable per-row id (tenantId:locationId) — tenantId repeats across a salon's location rows. */
  key: string;
  subdomain: string;
  name: string;
  bio: string | null;
  coverImageUrl: string | null;
  /** CSS object-position from the salon's hero focus point, e.g. "50% 30%". */
  coverImagePosition?: string | null;
  featured: boolean;
  distanceKm: number | null;
  /** Tenant display currency (ISO 4217, e.g. "EUR", "BRL") for every price on the row. */
  currency?: string;
  topServices: { name: string; price: number; durationMinutes: number }[];
  /** Null for mobile-only salons (no physical location, so no coordinates and no map pin). */
  location: { name: string; address: string; lat: number | null; lng: number | null } | null;
  /**
   * Display city, top-level (backend cb77a9d+). Mobile-only rows carry it here
   * because they have no `location`; older backends omit it.
   */
  city?: string;
  isMobile?: boolean;
  serviceArea?: string;
  /** ISO-2 of the row's location (backend sends it; null when unknown). */
  country?: string | null;

  // ── NEW (§1) — optional until the backend ships ──
  /** Null = no published reviews yet → UI shows "New", never a fake score. */
  rating?: number | null;
  reviewCount?: number;
  /** Listed + APPROVED = verified by our team. Contract says literal `true`. */
  verified?: boolean;
  /** Mobile salons: "travels 25 km". */
  travelRadiusKm?: number | null;
  /** ≤ 2. */
  matchingServices?: MatchingService[];
  /** Price for the map pin. */
  cheapestMatchingPrice?: number | null;
  /** Null = nothing in the lookahead window. */
  nextAvailable?: NextAvailable | null;
  /** Location amenity "Accessible" (as built, M2). */
  accessible?: boolean;
}

/** Contract name for a search row (alias of the existing `SalonSearchResult`). */
export type SearchRow = SalonSearchResult;

export interface SearchFacets {
  categories: { key: string; count: number }[];
  /** Rows with the "Accessible" amenity; ignores the `accessible` filter (as built, M2). D-06. */
  accessibleCount?: number;
}

export interface SearchResponse {
  results: SearchRow[];
  total: number;
  page: number;
  size: number;
  /** NEW. Rows outside the primary area; never mixed into `results`. */
  furtherAfield?: SearchRow[];
  /** NEW. Sidebar counts. */
  facets?: SearchFacets;
}

export type SearchWhen = 'today' | '7d' | 'any';
export type SearchSort = 'recommended' | 'distance' | 'rating' | 'price';

export interface SearchParams {
  category?: string | null;
  subcategory?: string | null;
  q?: string | null;
  city?: string | null;
  /**
   * ISO-2 (uppercase) country code derived from the hostname (BE/PT/FR/NL on
   * the country domains, null on salunas.com/localhost). When set, restricts
   * results to salons in that country. Maps to `country=XX` on
   * /marketplace/search.
   */
  country?: string | null;
  lat?: number | null;
  lng?: number | null;
  radius?: number | null;
  page?: number;
  size?: number;
  /**
   * When true, restrict results to salons that offer ONLINE (video-call)
   * bookings. Maps to `onlineAvailable=true` on /marketplace/search. (#249 A5)
   */
  onlineAvailable?: boolean;
  /** Narrows to salons offering mobile/home-visit bookings. Maps to mobileAvailable=true. */
  mobileAvailable?: boolean;
  /**
   * Audience facet (V92): CSV of MEN/WOMEN/KIDS. Keeps salons with at least one
   * service suiting every requested audience (unflagged services count as
   * "for everyone"). Maps to audience=... on /marketplace/search.
   */
  audience?: string | null;

  // ── NEW (§1) ──
  /** Only salons with a free slot in that window. Server default `any`. */
  when?: SearchWhen | null;
  /** Server default `recommended`. */
  sort?: SearchSort | null;
  /** e.g. 4 — requires reviews. */
  minRating?: number | null;
  /** Cheapest matching service ≤ value. */
  priceMax?: number | null;
  /** Only salons that can take online payment. */
  payOnline?: boolean;
  /** Location amenity "Accessible". */
  accessible?: boolean;
  /** 0..3 next-available slots per row (server default 3). */
  nextSlots?: number | null;
}

// ─── §2 Salon profile ──────────────────────────────────────────────────

export interface SalonLocation {
  id: string;
  name: string;
  address: string;
  phone: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string | null;
}

export interface SalonService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number;
  requiresDeposit: boolean;
  depositAmount: number | null;
  /** True when the tenant offers this service at the customer's location (satellite/mobile). */
  mobileEligible?: boolean;
  /** Pre-resolved travel fee for this service, if mobileEligible. Null = no fee configured. */
  resolvedRelocationFee?: number | null;
  /**
   * True when the tenant + service support ONLINE bookings (Google Meet video
   * call). Combined with tenant.onlineBookingsEnabled — both must be true for
   * the ModeChooser to surface the Video-call option. (#249 A3+)
   */
  onlineEligible?: boolean;
  /**
   * Fixed locations this service is explicitly offered at. Empty = offered
   * everywhere; combined with mobile/onlineEligible it marks a service as
   * alternative-mode-only (no AT_LOCATION option). Mirrors the tenant
   * booking page contract.
   */
  locationIds?: string[];
  /** Audience flags (V92): MEN/WOMEN/KIDS the service is aimed at; empty = everyone. */
  audiences?: string[];
  /**
   * Amount the customer must prepay for this service when the salon has
   * prepayment enabled (Task 10 BE). Currency comes from
   * salon.prepayment.currency. Used to drive the "Pay now ({amountDue})"
   * action + the BookingPaymentStep amount.
   */
  amountDue?: number;

  // ── NEW (§2) ──
  /** e.g. "consultation included" (future: tenant-editable). */
  note?: string | null;
  /** Render as "from €95". */
  priceFrom?: boolean;
}

export interface SalonServiceCategory {
  key: string;
  displayName: string;
  services: SalonService[];
}

/** One Salunas header photo with the salon's layout choices. */
export interface SalonPhoto {
  url: string;
  /** CSS object-position, e.g. "30% 70%". */
  position: string;
  fit: 'cover' | 'contain';
  /** 100–200 (percent); ignored for `contain`. */
  zoom: number;
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface OpeningHoursDay {
  day: IsoWeekday;
  /** Empty = closed that day. */
  ranges: { open: WallClockTime; close: WallClockTime }[];
}

export interface CancellationPolicy {
  allowCancellation: boolean;
  allowReschedule: boolean;
  /** From customBranding.booking (default 24); replaces the FE's hardcoded 24. */
  cancellationHours: number;
  rescheduleHours: number;
}

export interface QuickPickSlot {
  date: IsoDate;
  time: WallClockTime;
  serviceId: string;
}

export interface SalonPrepayment {
  enabled: boolean;
  allowPayAtLocation: boolean;
  currency: string;
  // Effective gate from the server: prepayment is enabled AND the salon has a
  // payout-ready Stripe Connect account. When false, the salon can't actually
  // receive online payments, so the UI must not offer them.
  paymentsAvailable?: boolean;
}

export interface SalonProfile {
  subdomain: string;
  name: string;
  bio: string | null;
  coverImageUrl: string | null;
  /** CSS object-position from the salon's hero focus point, e.g. "50% 30%". */
  coverImagePosition?: string | null;
  /**
   * Salunas header photos in the salon's order (Settings → Branding →
   * Salunas photos). Absolute URLs. Empty/absent → use coverImageUrl.
   */
  photos?: SalonPhoto[];
  featured: boolean;
  /**
   * Tenant display currency (ISO 4217, e.g. "EUR", "BRL") for every service
   * price / fee on the profile. Distinct from `prepayment.currency`, which is
   * the online-payment currency and only applies to prepay amounts.
   */
  currency?: string;
  website: string | null;
  locations: SalonLocation[];
  serviceCategories: SalonServiceCategory[];
  /**
   * Tenant-level flag for satellite/mobile bookings — true when at least one
   * service is mobile-eligible. Exposed by MarketplaceResource. (#246/#247)
   */
  mobileBookingsEnabled?: boolean;
  /**
   * Tenant-level flag for ONLINE (video-call) bookings. Combined with
   * service.onlineEligible to decide whether to show the Video-call mode in
   * the ModeChooser. Exposed by MarketplaceResource. (#249 A3+)
   */
  onlineBookingsEnabled?: boolean;
  /**
   * Tenant-level IANA timezone (branding tz, else first FIXED location). Used
   * to convert ONLINE slot times when no physical location is selected — the
   * hidden dispatch location no longer appears in `locations`.
   */
  timezone?: string | null;
  /**
   * Salon-level prepayment configuration (Task 10 BE). When `enabled`, the
   * booking dialog branches into the prepayment flow: customers pay up-front
   * via Stripe. `allowPayAtLocation` adds a second "pay at the location"
   * action alongside the pay-now path; when false, only prepayment is allowed
   * (a plain book would be 402-rejected). Absent/undefined = prepayment off.
   */
  prepayment?: SalonPrepayment;

  // ── NEW (§2) — optional until the backend ships ──
  verified?: boolean;
  rating?: number | null;
  reviewCount?: number;
  /** Category key, for the breadcrumb. */
  primaryCategory?: string | null;
  travelRadiusKm?: number | null;
  /** Primary FIXED location, ISO weekdays. */
  openingHours?: OpeningHoursDay[];
  /** e.g. ["Accessible", "Parking"]. */
  amenities?: string[];
  /** Service-agnostic: the most-booked service, else the cheapest. */
  nextAvailable?: NextAvailable | null;
  /** ≤ 3, across days. */
  quickPick?: QuickPickSlot[];
  /** Absent on older backends → callers fall back to a 24 h default. */
  cancellationPolicy?: CancellationPolicy | null;
  /** The salon approves bookings by hand: they land PENDING (M2 backend, booking-rules). */
  requiresApproval?: boolean;
  /** Legacy free-text area for mobile salons ("Oostende en 20 km rond"); shown when there is no `travelRadiusKm`. */
  serviceAreaDescription?: string | null;
  /** ≤ 2, newest published. */
  reviewsPreview?: Review[];
}

// ─── Employees & single-day availability (existing) ────────────────────

export interface MarketplaceEmployee {
  id: string;
  firstName: string;
  lastName: string | null;
  role: string | null;
  avatar: string | null;
}

export interface AvailabilityResponse {
  date: string;
  slots: string[]; // "HH:mm"
}

// ─── §3 Multi-day availability ─────────────────────────────────────────

export interface AvailabilityRangeSlot {
  time: WallClockTime;
  /** Taken slots are included with `available: false` (struck through). */
  available: boolean;
}

export interface AvailabilityRangeDay {
  date: IsoDate;
  /** Salon closed that day ("Closed" in the date strip). */
  closed: boolean;
  slots: AvailabilityRangeSlot[];
}

export interface AvailabilityRange {
  timezone: string;
  days: AvailabilityRangeDay[];
  /** Last bookable date (advanceBookingDays). */
  bookableUntil: IsoDate;
}

export interface AvailabilityRangeParams {
  serviceId: string | null;
  /** First day, "YYYY-MM-DD". */
  from: IsoDate | null;
  /** Server default 7. */
  days?: number;
  employeeId?: string | null;
  locationId?: string | null;
  bookingMode?: BookingMode;
  /**
   * Reschedule: the booking being moved, so its own time counts as free
   * (backend M6; older backends ignore the param).
   */
  excludeAppointmentId?: string | null;
}

// ─── §4 Slot holds ─────────────────────────────────────────────────────

export interface CreateHoldRequest {
  serviceId: string;
  date: IsoDate;
  time: WallClockTime;
  employeeId?: string | null;
  locationId?: string | null;
  bookingMode: BookingMode;
}

export interface Hold {
  holdId: string;
  /** Salon-local datetime. */
  expiresAt: WallClockDateTime;
  /** The employee the hold was assigned to (resolved server-side for "anyone"). */
  employeeId: string;
}

// ─── §5 Booking ────────────────────────────────────────────────────────

export interface BookingPayload {
  serviceId: string;
  // Nullable/optional: ONLINE and MOBILE bookings have no physical location.
  locationId?: string | null;
  employeeId?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  notes?: string;
  // Guest-only — ignored by BE when a session is present.
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /**
   * Customer-chosen language at booking time (en/nl/fr/pt-BR). BE
   * normalizes and persists to appointments.customer_language; drives
   * the confirmation/cancellation email language.
   */
  language?: string;
  /**
   * Marketing opt-in checkbox state (#162). Tri-state on the wire:
   * omitted = no change to the customer's stored preference; true =
   * opt in; false = opt out. The FE only ever sends `true` when ticked
   * and otherwise omits the field, per spec ("Never silently downgrade").
   */
  marketingOptIn?: boolean;
  /**
   * AT_LOCATION (default), MOBILE, or ONLINE. Sent when the service supports
   * the chosen mode. ONLINE requires service.onlineEligible AND
   * tenant.onlineBookingsEnabled (BE re-validates).
   */
  bookingMode?: BookingMode;
  /** Customer's destination address. Only sent when bookingMode === 'MOBILE'. */
  customerAddress?: string;
  /** Coordinates of the destination address (from the address suggestion), MOBILE only (D-135). */
  customerLat?: number;
  customerLng?: number;
  /**
   * Customer's IANA timezone (e.g. "America/Sao_Paulo"). REQUIRED when
   * bookingMode === 'ONLINE' — BE rejects with customer_timezone_required.
   * Auto-detected from the browser at booking time and editable on the
   * confirm step via the TimezoneConfirm control.
   */
  customerTimezone?: string;
  /**
   * Prepayment "pay at the location" path (Task 10/20). When the salon has
   * prepayment enabled AND allows pay-at-location, sending `true` books
   * without an up-front Stripe charge. Omitted on the normal/non-prepay path.
   * (A plain book against a prepay-required salon is 402-rejected by the BE.)
   */
  payAtLocation?: boolean;
  /**
   * REQUIRED by the BE — the customer must have agreed to the cancellation
   * policy. The booking endpoint rejects with 400 when absent/false. The UI
   * gates the Book button behind the policy checkbox, so this is always `true`.
   */
  agreedToPolicy?: boolean;
  /** Email-OTP proof from /booking-verifications/confirm; sent only when required. */
  verificationToken?: string;
  /** NEW (§4/§5). Consumed by the booking when valid. */
  holdId?: string;
}

export interface BookingResponse {
  bookingReference: string;
  appointmentId: string;
  status: string;
  approvalRequired: boolean;
  salon: { subdomain: string; name: string };
  serviceName: string;
  locationName: string;
  startTime: string;
  endTime: string;
  price: number;
  signedIn: boolean;
  /** Echoed by the BE when supported; falls back to the mode the FE sent. */
  bookingMode?: BookingMode;
  /**
   * Lifecycle of the online-meeting link.
   * NOT_APPLICABLE for non-ONLINE bookings; LINK_READY/LINK_PENDING for ONLINE.
   * Treated as LINK_PENDING when absent on an ONLINE booking.
   */
  linkState?: 'NOT_APPLICABLE' | 'LINK_READY' | 'LINK_PENDING';
  /**
   * Google Meet URL when linkState === 'LINK_READY'. Absent (or null) when the
   * link is still pending or the booking isn't ONLINE.
   */
  onlineMeetingUrl?: string | null;
  /** Echoed by the BE when supported; falls back to the customerTimezone the FE sent. */
  customerTimezone?: string | null;

  // ── NEW (§5) ──
  /** salunas.<tld>/me/bookings?b=<id> (magic-link sign-in if needed). */
  manageUrl?: string | null;
  /** GET /bookings/{bookingReference}/ics?t=<managementToken>. */
  calendarIcsUrl?: string | null;
  /** Salon-local datetime. */
  cancellationDeadline?: WallClockDateTime | null;
  customerEmail?: string | null;
}

/** Contract name for the booking response (alias of `BookingResponse`). */
export type BookingResult = BookingResponse;

export interface StartVerificationResult {
  verificationRequired: boolean;
}

export interface ConfirmVerificationResult {
  verificationToken: string;
}

// ─── Account / §6 My bookings ──────────────────────────────────────────

export interface MarketplaceUser {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  city: string | null;
  language: string | null;
}

export interface MyBooking {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  tenant: { subdomain: string; name: string; listed?: boolean } | null;
  /** Salon display currency (ISO 4217) for `service.price`. */
  currency?: string;
  /** `durationMinutes` as built in M5 (end = start + duration on the account page). */
  service: { id: string; name: string; price: number; durationMinutes?: number } | null;
  /** Omitted (or null) for MOBILE bookings as built in M5. */
  location?: { id: string; name: string; address: string } | null;

  // ── NEW (§6) — optional until the backend ships ──
  bookingReference?: string;
  bookingMode?: BookingMode;
  customerAddress?: string | null;
  isMobileSalon?: boolean;
  employee?: { id: string; firstName: string } | null;
  /** Absent → treat as false (hide the action). */
  canCancel?: boolean;
  canReschedule?: boolean;
  /** Salon-local datetime. */
  cancellationDeadline?: WallClockDateTime | null;
  timezone?: string | null;
  /** Past bookings only. */
  review?: { rating: number } | null;
  canReview?: boolean;
  calendarIcsUrl?: string | null;
  /** FIXED location only. */
  directionsUrl?: string | null;
}

export interface RescheduleRequest {
  date: IsoDate;
  time: WallClockTime;
  employeeId?: string | null;
  holdId?: string | null;
}

/** `GET /me/rebook-suggestion` (204 → null). */
export interface RebookSuggestion {
  // As built in M5 (api-contracts.md "As built in M2/M5").
  tenant: { subdomain: string; name: string; isMobile?: boolean };
  service: { id: string; name: string; price?: number; durationMinutes?: number; currency?: string };
  bookingMode?: BookingMode;
  locationId?: string | null;
  /** ≤ 3, may be empty. */
  slots: { date: IsoDate; time: WallClockTime }[];
  /** Legacy top-level currency; as built it lives on `service.currency`. */
  currency?: string;
  timezone?: string | null;
}

// ─── §7 Preferences ────────────────────────────────────────────────────

export interface Preferences {
  emailReminder24h: boolean;
  /** WhatsApp, not SMS (D-01). */
  whatsappReminder2h: boolean;
  /**
   * Review-request email after a visit (backend 48fc1eb, default true).
   * Absent on older backends → the checkbox is hidden.
   */
  reviewRequests?: boolean;
}

// ─── §8 Reviews ────────────────────────────────────────────────────────

export type ReviewRating = 1 | 2 | 3 | 4 | 5;

export interface Review {
  id: string;
  /** "Els V.": first name + last initial. */
  authorDisplay: string;
  rating: ReviewRating;
  text: string | null;
  serviceName: string;
  createdAt: WallClockDateTime;
  /** Future, admin side. */
  salonReply?: { text: string; createdAt: WallClockDateTime } | null;
}

export interface ReviewsPage {
  items: Review[];
  total: number;
  /** Null when there are no published reviews. */
  rating: number | null;
  reviewCount: number;
  /** As built (M6): JSON keys "1".."5" → count of published reviews. */
  distribution: Partial<Record<ReviewRating, number>>;
  /** As built (M6): echo of the 0-based page / size. */
  page?: number;
  size?: number;
}

/** Max review text length (code points on the server; we cap UTF-16 units). */
export const REVIEW_TEXT_MAX = 1000;

export interface SubmitReviewRequest {
  rating: ReviewRating;
  /** ≤ 1000 chars. */
  text?: string;
}

/**
 * `details.reason` of `403 not_eligible` (as built, M6). `too_old` = more
 * than 90 days after the visit.
 */
export type ReviewIneligibleReason = 'not_completed' | 'cancelled' | 'no_show' | 'expired' | 'too_old';

// ─── §9 Waitlist / alerts ──────────────────────────────────────────────

export interface JoinWaitlistRequest {
  email: string;
  serviceId: string;
  from: IsoDate;
  to: IsoDate;
  employeeId?: string | null;
  /** As built (M6): the AT_LOCATION branch; default = first offering the service. */
  locationId?: string | null;
  language: string;
}

/**
 * `201 { id, status? }`. Errors (as built, M6): 409 `waitlist_limit_reached`
 * (no counts), 409 `slots_available` (`SlotsAvailableDetails`), 429 `rate_limited` (+ Retry-After), 400 `invalid_request`
 * with `details.field` / `details.reason` (`in_past`, `range_too_long`,
 * `beyond_bookable_until`, …). The range is ≤ 30 days, not in the past and
 * ≤ `bookableUntil`. With a session, the session email wins.
 */
export interface JoinWaitlistResponse {
  id: string;
  /**
   * Double opt-in (M6 follow-up): anonymous subscriptions wait for the email
   * link (`pending_confirmation`); signed-in ones are `active` at once.
   * Missing (older backend) → treat as `active`.
   */
  status?: SubscriptionStatus;
}

export type SubscriptionStatus = 'pending_confirmation' | 'active';

/**
 * `details` of 409 `slots_available` on `POST /salon/{s}/waitlist`: the range
 * already has a free time, so there is nothing to wait for.
 */
export interface SlotsAvailableDetails {
  date: IsoDate;
  time: WallClockTime;
  employeeId?: string | null;
  /** As built (backend 48fc1eb): the service and branch of the free slot. */
  serviceId?: string | null;
  locationId?: string | null;
}

/**
 * `POST /alerts/new-salons` → `201 { id }` — also for an identical existing
 * subscription (same id). Needs `country` OR both `lat` + `lng`. Errors:
 * 409 `alert_limit_reached` (no counts), 429 `rate_limited`, 400 `invalid_request`.
 */
export interface NewSalonAlertRequest {
  email: string;
  category?: string | null;
  /** Omitted when the search has no city (contract: optional in practice, never ""). */
  city?: string;
  /** ISO-2; from the country domain, else from the results. Omitted when unknown. */
  country?: string;
  /** Geo searches ("Near me", map area): both or neither. */
  lat?: number;
  lng?: number;
  language: string;
}

export interface NewSalonAlertResponse {
  id: string;
  /** See `JoinWaitlistResponse.status`. */
  status?: SubscriptionStatus;
}
